import {
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import {
  IActivo,
  IDispositivoAlarma,
  INota,
  IPopulate,
  IQueryParam,
  ITracker,
} from 'modelos/src';
import {
  Subject,
  Subscription,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs';
import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';
import { NotasService } from '../../../../auxiliares/servicios/http/notas.service';
import { TrackersService } from '../../../../auxiliares/servicios/http/trackers.service';
import { SimsService } from '../../../../auxiliares/servicios/http/sims.service';
import { AlarmasDomiciliariasService } from '../../alarmas/alarmas-domiciliarias/alarmas-domiciliarias.service';

/**
 * Un resultado de la búsqueda por teléfono, normalizado para las tarjetas.
 * Se alimenta desde distintas entidades (Contactos, Alarmas, Trackers, etc.).
 */
export interface IResultadoBusquedaTelefono {
  entidad: string; // Tipo de entidad (badge). Ej: 'Contacto', 'Alarma'
  icono: string; // Material icon
  colorKey: string; // Clave de color (css-safe). Ej: 'tracker', 'vehiculo'
  titulo: string; // Nombre principal de la entidad encontrada
  tituloChip?: string; // Chip antes del título (ej. entidad asignada del contacto)
  cliente?: string; // Cliente al que pertenece (segunda línea)
  detalle?: string; // Info extra. Ej: nombre del contacto
  campo: string; // Campo donde matcheó. Ej: 'Teléfono', 'SIM 1'
  valor: string; // Valor del campo que matcheó
  ruta?: string[]; // routerLink a los detalles de la entidad (lupa)
  rutaTooltip?: string; // tooltip de la lupa

  // --- Alternancia (ej. tracker con un activo/vehículo asignado) ---
  // Si viene la data de asignado, la tarjeta muestra dos íconos para alternar
  // entre la entidad principal y la asignada (cada una con su color).
  asignadoEntidad?: string; // Ej: 'Vehículo'
  asignadoIcono?: string;
  asignadoColorKey?: string;
  asignadoTitulo?: string;
  rutaAsignado?: string[];
  vista?: 'principal' | 'asignado'; // estado UI de la tarjeta
}

@Component({
  selector: 'app-busqueda-telefono',
  templateUrl: './busqueda-telefono.component.html',
  styleUrl: './busqueda-telefono.component.scss',
  imports: [AuxiliaresModule, RouterModule],
  encapsulation: ViewEncapsulation.None,
})
export class BusquedaTelefonoComponent implements OnInit, OnDestroy {
  private notasService = inject(NotasService);
  private alarmasService = inject(AlarmasDomiciliariasService);
  private trackersService = inject(TrackersService);
  private simsService = inject(SimsService);

  public termino = '';
  public buscando = false;
  public busco = false; // true una vez que se ejecutó al menos una búsqueda
  public resultados: IResultadoBusquedaTelefono[] = [];

  // Longitud mínima para disparar la búsqueda (evita traer todo)
  private readonly minCaracteres = 3;

  private termino$ = new Subject<string>();
  private subs?: Subscription;

  // --- CONTACTOS ---
  // Solo traemos lo necesario: nombre del cliente, la entidad asignada
  // (idAsignado puede ser una alarma, activo o luminaria) y el campo que matchea.
  private readonly populateContacto: IPopulate[] = [
    { path: 'cliente', select: 'nombre' },
    { path: 'alarma', select: 'nombre numeroAbonado' },
    { path: 'activo', select: 'identificacion categoria' },
    { path: 'luminaria', select: 'identificacion' },
  ];
  private readonly selectContacto =
    'tipo informacion.telefono informacion.contacto idCliente idAsignado';

  // --- ALARMAS ---
  private readonly populateAlarma: IPopulate[] = [
    { path: 'cliente', select: 'nombre' },
    { path: 'sim1', select: 'numero' },
    { path: 'sim2', select: 'numero' },
  ];
  private readonly selectAlarma =
    'nombre numeroAbonado idSim1 idSim2 idCliente';

  // --- TRACKERS ---
  private readonly populateTracker: IPopulate[] = [
    { path: 'cliente', select: 'nombre' },
    { path: 'activo', select: 'identificacion categoria' },
    { path: 'sim1', select: 'numero' },
    { path: 'sim2', select: 'numero' },
  ];
  private readonly selectTracker =
    'nombre identificacion telefono.deviceId idSim1 idSim2 idCliente asignadoA';

  ngOnInit(): void {
    this.subs = this.termino$
      .pipe(debounceTime(350), distinctUntilChanged())
      .subscribe((termino) => this.ejecutarBusqueda(termino));
  }

  ngOnDestroy(): void {
    this.subs?.unsubscribe();
  }

  public onTerminoChange(valor: string): void {
    this.termino = valor;
    this.termino$.next(valor);
  }

  public limpiar(): void {
    this.termino = '';
    this.resultados = [];
    this.busco = false;
    this.buscando = false;
  }

  private async ejecutarBusqueda(terminoRaw: string): Promise<void> {
    const termino = terminoRaw?.trim();
    if (!termino || termino.length < this.minCaracteres) {
      this.resultados = [];
      this.busco = false;
      this.buscando = false;
      return;
    }

    this.buscando = true;
    try {
      const [contactos, alarmas, trackers] = await Promise.all([
        this.buscarContactos(termino),
        this.buscarAlarmas(termino),
        this.buscarTrackers(termino),
      ]);
      this.resultados = [...contactos, ...alarmas, ...trackers];
    } finally {
      this.buscando = false;
      this.busco = true;
    }
  }

  // --- CONTACTOS (notas de tipo 'Contacto') ---
  private async buscarContactos(
    termino: string,
  ): Promise<IResultadoBusquedaTelefono[]> {
    const filter = {
      tipo: 'Contacto',
      'informacion.telefono': {
        $regex: this.escaparRegex(termino),
        $options: 'i',
      },
    };

    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      select: this.selectContacto,
      populate: JSON.stringify(this.populateContacto),
      limit: 50,
      sort: 'informacion.contacto',
      // Incluir contactos del cliente logueado y de toda su jerarquía de hijos.
      includeChildren: true,
    };

    const listado = await this.notasService.getFiltered(query);
    return (listado?.datos || []).map((nota: INota) => {
      const asignado = this.resolverAsignado(nota);
      return {
        entidad: 'Contacto',
        icono: 'contacts',
        colorKey: 'contacto',
        // Primero DE QUÉ es (entidad asignada), luego DE QUÉ cliente.
        tituloChip: asignado?.tipo,
        titulo: asignado?.texto || nota.cliente?.nombre || 'Sin cliente',
        cliente: asignado ? nota.cliente?.nombre : undefined,
        detalle: nota.informacion?.contacto,
        campo: 'Teléfono',
        valor: nota.informacion?.telefono || '',
        ruta: asignado?.ruta,
      };
    });
  }

  // --- ALARMAS (dispositivos de alarma) — match por SIM ---
  private async buscarAlarmas(
    termino: string,
  ): Promise<IResultadoBusquedaTelefono[]> {
    // La SIM es una entidad aparte: primero resolvemos los _id de las SIM cuyo
    // número matchea y luego filtramos alarmas por idSim1/idSim2 $in esos ids.
    const idsSim = await this.simsService.idsPorNumero(
      this.escaparRegex(termino),
    );
    if (idsSim.length === 0) {
      return [];
    }
    const filter = {
      $or: [{ idSim1: { $in: idsSim } }, { idSim2: { $in: idsSim } }],
    };

    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      select: this.selectAlarma,
      populate: JSON.stringify(this.populateAlarma),
      limit: 50,
      sort: 'nombre',
      includeChildren: true,
    };

    const listado = await this.alarmasService.getFiltered(query);
    return (listado?.datos || []).map((alarma: IDispositivoAlarma) => {
      const sim = this.simQueMatchea(alarma, termino);
      const id = alarma._id;
      return {
        entidad: 'Alarma',
        icono: 'security',
        colorKey: 'alarma',
        titulo: alarma.nombre || alarma.numeroAbonado || 'Sin nombre',
        cliente: alarma.cliente?.nombre,
        campo: sim.label,
        valor: sim.valor,
        ruta: id
          ? ['/main', 'alarmas', 'alarmasDomiciliarias', 'detallesAlarma', id]
          : undefined,
      };
    });
  }

  // --- TRACKERS — match por teléfono (deviceId) o SIM ---
  private async buscarTrackers(
    termino: string,
  ): Promise<IResultadoBusquedaTelefono[]> {
    const regex = { $regex: this.escaparRegex(termino), $options: 'i' };
    // La SIM es una entidad aparte: resolvemos los _id de las SIM cuyo número
    // matchea para filtrar por idSim1/idSim2. El match por teléfono (deviceId)
    // del tracker se mantiene embebido.
    const idsSim = await this.simsService.idsPorNumero(
      this.escaparRegex(termino),
    );
    const or: any[] = [{ 'telefono.deviceId': regex }];
    if (idsSim.length) {
      or.push({ idSim1: { $in: idsSim } });
      or.push({ idSim2: { $in: idsSim } });
    }
    const filter = { $or: or };

    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      select: this.selectTracker,
      populate: JSON.stringify(this.populateTracker),
      limit: 50,
      sort: 'nombre',
      includeChildren: true,
    };

    const listado = await this.trackersService.getFiltered(query);
    return (listado?.datos || []).map((tracker: ITracker) => {
      const campo = this.campoTrackerQueMatchea(tracker, termino);
      const id = tracker._id;
      // Entidad asignada (si tiene): el tracker se asigna a un activo.
      const asignado = tracker.activo
        ? this.resolverActivo(tracker.activo, tracker.asignadoA)
        : undefined;
      return {
        entidad: 'Tracker',
        icono: 'gps_fixed',
        colorKey: 'tracker',
        // Prioriza el tracker como título.
        titulo: tracker.nombre || tracker.identificacion || 'Sin nombre',
        cliente: tracker.cliente?.nombre,
        campo: campo.label,
        valor: campo.valor,
        ruta: id
          ? ['/main', 'administracion', 'trackers', 'detallesTracker', id]
          : undefined,
        rutaTooltip: 'Ver tracker',
        // Datos de alternancia: la entidad asignada (activo/vehículo/colectivo).
        asignadoEntidad: asignado?.tipo,
        asignadoIcono: asignado ? this.iconoDeTipo(asignado.tipo) : undefined,
        asignadoColorKey: asignado ? this.colorKeyDe(asignado.tipo) : undefined,
        asignadoTitulo: asignado?.texto,
        rutaAsignado: asignado?.ruta,
        vista: asignado ? 'principal' : undefined,
      };
    });
  }

  // --- Helpers de vista/alternancia ---

  public alternarVista(
    r: IResultadoBusquedaTelefono,
    vista: 'principal' | 'asignado',
  ): void {
    if (r.asignadoEntidad) r.vista = vista;
  }

  public vistaEsAsignado(r: IResultadoBusquedaTelefono): boolean {
    return r.vista === 'asignado' && !!r.asignadoEntidad;
  }

  // Valores "activos" según la vista actual de la tarjeta
  public entidadActiva(r: IResultadoBusquedaTelefono): string {
    return this.vistaEsAsignado(r) ? r.asignadoEntidad! : r.entidad;
  }
  public iconoActivo(r: IResultadoBusquedaTelefono): string {
    return this.vistaEsAsignado(r) ? r.asignadoIcono || 'category' : r.icono;
  }
  public colorActivo(r: IResultadoBusquedaTelefono): string {
    return this.vistaEsAsignado(r)
      ? r.asignadoColorKey || 'activo'
      : r.colorKey;
  }
  public tituloActivo(r: IResultadoBusquedaTelefono): string {
    return this.vistaEsAsignado(r) ? r.asignadoTitulo || '' : r.titulo;
  }
  public rutaActiva(r: IResultadoBusquedaTelefono): string[] | undefined {
    return this.vistaEsAsignado(r) ? r.rutaAsignado : r.ruta;
  }

  private iconoDeTipo(tipo: string): string {
    switch (this.colorKeyDe(tipo)) {
      case 'vehiculo':
        return 'directions_car';
      case 'colectivo':
        return 'directions_bus';
      case 'alarma':
        return 'security';
      case 'luminaria':
        return 'lightbulb';
      default:
        return 'category';
    }
  }

  // Normaliza un tipo a una clave css-safe (minúsculas, sin acentos).
  private colorKeyDe(tipo: string): string {
    return tipo.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  }

  // Determina qué campo (teléfono/SIM) matcheó en el tracker.
  private campoTrackerQueMatchea(
    tracker: ITracker,
    termino: string,
  ): { label: string; valor: string } {
    const t = termino.toLowerCase();
    const tel = tracker.telefono?.deviceId;
    const sim1 = tracker.sim1?.numero;
    const sim2 = tracker.sim2?.numero;
    if (tel && tel.toLowerCase().includes(t)) {
      return { label: 'Teléfono', valor: tel };
    }
    if (sim1 && sim1.toLowerCase().includes(t)) {
      return { label: 'SIM 1', valor: sim1 };
    }
    if (sim2 && sim2.toLowerCase().includes(t)) {
      return { label: 'SIM 2', valor: sim2 };
    }
    return { label: 'Teléfono', valor: tel || sim1 || sim2 || '' };
  }

  // Determina qué SIM matcheó el término para mostrarlo en la tarjeta.
  private simQueMatchea(
    alarma: IDispositivoAlarma,
    termino: string,
  ): { label: string; valor: string } {
    const t = termino.toLowerCase();
    const sim1 = alarma.sim1?.numero;
    const sim2 = alarma.sim2?.numero;
    if (sim1 && sim1.toLowerCase().includes(t)) {
      return { label: 'SIM 1', valor: sim1 };
    }
    if (sim2 && sim2.toLowerCase().includes(t)) {
      return { label: 'SIM 2', valor: sim2 };
    }
    // Fallback (no debería pasar): mostrar el que exista
    return { label: 'SIM', valor: sim1 || sim2 || '' };
  }

  // idAsignado puede apuntar a una alarma, un activo o una luminaria.
  // Devuelve el tipo, un identificador legible y la ruta a sus detalles.
  private resolverAsignado(
    nota: INota,
  ): { tipo: string; texto: string; ruta?: string[] } | undefined {
    const id = nota.idAsignado;
    if (nota.alarma) {
      return {
        tipo: 'Alarma',
        texto: nota.alarma.nombre || nota.alarma.numeroAbonado || id || '',
        ruta: id
          ? ['/main', 'alarmas', 'alarmasDomiciliarias', 'detallesAlarma', id]
          : undefined,
      };
    }
    if (nota.activo) {
      return this.resolverActivo(nota.activo, id);
    }
    if (nota.luminaria) {
      return {
        tipo: 'Luminaria',
        texto: nota.luminaria.identificacion || id || '',
        ruta: id
          ? ['/main', 'luminarias', 'luminarias', 'detalles', id]
          : undefined,
      };
    }
    // Asignado pero no se pudo resolver la entidad
    if (id) {
      return { tipo: 'Asignado', texto: id };
    }
    return undefined;
  }

  // Resuelve un activo según su categoría (Vehículo / Colectivo / Normal→Activo):
  // tipo legible + ruta al detalle correspondiente.
  private resolverActivo(
    activo: IActivo,
    id?: string,
  ): { tipo: string; texto: string; ruta?: string[] } {
    const texto = activo.identificacion || id || '';
    if (activo.categoria === 'Vehículo') {
      return {
        tipo: 'Vehículo',
        texto,
        ruta: id
          ? ['/main', 'vehiculos', 'vehiculos', 'detalles', id]
          : undefined,
      };
    }
    if (activo.categoria === 'Colectivo') {
      return {
        tipo: 'Colectivo',
        texto,
        ruta: id
          ? ['/main', 'colectivos', 'colectivos', 'detalles', id]
          : undefined,
      };
    }
    return {
      tipo: 'Activo',
      texto,
      ruta: id ? ['/main', 'activos', 'activos', 'detalles', id] : undefined,
    };
  }

  private escaparRegex(texto: string): string {
    return texto.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
